import React, { useState } from "react";
import client from "client/client";

import {
  Authentication,
  deserializeBrands,
  getUserId,
  serializeBrands,
} from "../../utils/auth";
import { Brand, PersonStatus } from "@hubcapp/types";
import {
  INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
  INSTRUCTOR_BRAND_LOCAL_STORAGE_KEY,
  INSTRUCTOR_FIRST_NAME_LOCAL_STORAGE_KEY,
  INSTRUCTOR_ID_LOCAL_STORAGE_KEY,
  INSTRUCTOR_LAST_NAME_LOCAL_STORAGE_KEY,
  INSTRUCTOR_STATUS_LOCAL_STORAGE_KEY,
  STORYBOOK_DISABLE_AUTH_CHECK,
} from "@hubcapp/constants";

import { InstructorType } from "./types";

type AuthContextProps = {
  signIn: (adiNumber: string, password: string) => Promise<any>;
  confirmMFA: (accessCode: string) => Promise<any>;
  resendMFA: () => Promise<any>;
  resendMFAToResetPassword: () => Promise<any>;
  signUp: (adiNumber: string, mobileNumber: string) => Promise<any>;
  resetPassword: (accessCode: string, password: string) => Promise<any>;
  instructor: InstructorType;
  isAuthenticated: () => Promise<any>;
  signOut: () => Promise<any>;
  randomSignIn: (
    instructor: Pick<
      InstructorType,
      "id" | "brands" | "firstName" | "lastName" | "adiNumber" | "status"
    >,
  ) => void;
  randomSignInIsAuthenticated: () => Promise<any>;
  randomSignInSignOut: () => Promise<any>;
};

const userDataLocalKeys = [
  INSTRUCTOR_ID_LOCAL_STORAGE_KEY,
  INSTRUCTOR_BRAND_LOCAL_STORAGE_KEY,
  INSTRUCTOR_FIRST_NAME_LOCAL_STORAGE_KEY,
  INSTRUCTOR_LAST_NAME_LOCAL_STORAGE_KEY,
  INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
  INSTRUCTOR_STATUS_LOCAL_STORAGE_KEY,
];

const AuthContext = React.createContext<AuthContextProps>({
  signIn: () => new Promise(() => {}),
  confirmMFA: () => new Promise(() => {}),
  resendMFA: () => new Promise(() => {}),
  resendMFAToResetPassword: () => new Promise(() => {}),
  signUp: () => new Promise(() => {}),
  resetPassword: () => new Promise(() => {}),
  instructor: {
    id: STORYBOOK_DISABLE_AUTH_CHECK
      ? "7dd737ac-21a1-4b29-af09-cda9e28c4ee6"
      : "", //Do not like doing this but failed to mock this provider for Storybook
    brands: [Brand.Aads, Brand.Bsm],
    adiNumber: "",
    firstName: STORYBOOK_DISABLE_AUTH_CHECK ? "Zachery" : "", //Do not like doing this but failed to mock this provider for Storybook
    lastName: STORYBOOK_DISABLE_AUTH_CHECK ? "Grant" : "", //Do not like doing this but failed to mock this provider for Storybook
    mobileNumber: "",
    status: PersonStatus.Active,
  },
  isAuthenticated: () => new Promise(() => {}),
  signOut: () => new Promise(() => {}),
  randomSignIn: () => {},
  randomSignInIsAuthenticated: () => new Promise(() => {}),
  randomSignInSignOut: () => new Promise(() => {}),
});

const AuthProvider = (props: any) => {
  const [instructor, setInstructor] = useState({
    id: "",
    brands: [Brand.Aads],
    adiNumber: "",
    firstName: "",
    lastName: "",
    mobileNumber: "",
    status: PersonStatus.Active,
  });

  const [user, setUser] = useState<Authentication>(new Authentication(""));

  const randomSignIn = ({
    id,
    brands,
    firstName,
    lastName,
    adiNumber,
    status,
  }: Pick<
    InstructorType,
    "id" | "brands" | "firstName" | "lastName" | "adiNumber" | "status"
  >) => {
    window.localStorage.setItem(INSTRUCTOR_ID_LOCAL_STORAGE_KEY, id);
    window.localStorage.setItem(
      INSTRUCTOR_BRAND_LOCAL_STORAGE_KEY,
      serializeBrands(brands),
    );
    window.localStorage.setItem(
      INSTRUCTOR_FIRST_NAME_LOCAL_STORAGE_KEY,
      firstName,
    );
    window.localStorage.setItem(
      INSTRUCTOR_LAST_NAME_LOCAL_STORAGE_KEY,
      lastName,
    );

    window.localStorage.setItem(
      INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
      adiNumber,
    );

    window.localStorage.setItem(INSTRUCTOR_STATUS_LOCAL_STORAGE_KEY, status);

    setInstructor({
      ...instructor,
      id,
      brands,
      firstName,
      lastName,
      adiNumber,
      status,
    });
  };

  const randomSignInIsAuthenticated = () =>
    new Promise((resolve, reject) => {
      const instructorId = window.localStorage.getItem(
        INSTRUCTOR_ID_LOCAL_STORAGE_KEY,
      );

      const instructorBrands = deserializeBrands(
        window.localStorage.getItem(INSTRUCTOR_BRAND_LOCAL_STORAGE_KEY),
      );

      const instructorFirstName = window.localStorage.getItem(
        INSTRUCTOR_FIRST_NAME_LOCAL_STORAGE_KEY,
      );

      const instructorLastName = window.localStorage.getItem(
        INSTRUCTOR_LAST_NAME_LOCAL_STORAGE_KEY,
      );

      const instructorAdiNumber = window.localStorage.getItem(
        INSTRUCTOR_ADI_NUMBER_LOCAL_STORAGE_KEY,
      );

      const instructorStatus = window.localStorage.getItem(
        INSTRUCTOR_STATUS_LOCAL_STORAGE_KEY,
      );

      if (
        !instructorId ||
        !instructorBrands ||
        !instructorFirstName ||
        !instructorLastName ||
        !instructorAdiNumber ||
        !instructorStatus
      ) {
        return reject(false);
      }

      if (
        !instructor.id ||
        !instructor.brands ||
        !instructor.firstName ||
        !instructor.lastName ||
        !instructor.adiNumber ||
        !instructor.status
      ) {
        setInstructor({
          ...instructor,
          id: instructorId,
          brands: instructorBrands,
          firstName: instructorFirstName,
          lastName: instructorLastName,
          adiNumber: instructorAdiNumber,
          status: instructorStatus as PersonStatus,
        });
      }
      return resolve(true);
    });

  const randomSignInSignOut = () =>
    new Promise(resolve => {
      userDataLocalKeys.forEach(key => window.localStorage.removeItem(key));
      resolve(true);
    });

  const signIn = (adiNumber: string, password: string) => {
    const user = new Authentication(adiNumber);
    return user.signIn(adiNumber, password).then(data => {
      setUser(user);
      setInstructor({
        ...instructor,
        adiNumber,
      });
      return data;
    });
  };

  const signUp = (adiNumber: string, mobileNumber: string) => {
    const user = new Authentication(adiNumber);
    return user.sendMFAToResetPassword(mobileNumber).then(data => {
      setUser(user);
      setInstructor({
        ...instructor,
        adiNumber,
        mobileNumber,
      });
      return data;
    });
  };

  const confirmMFA = (accessCode: string) => user.confirmMFA(accessCode);

  const resendMFA = () => user.resendMFACode();

  const resendMFAToResetPassword = () =>
    user.sendMFAToResetPassword(instructor.mobileNumber).then(data => {
      setUser(user);
      setInstructor({
        ...instructor,
      });
      return data;
    });

  const resetPassword = (accessCode: string, password: string) =>
    user.resetPassword(accessCode, password);

  const isAuthenticated = () => {
    const user = new Authentication(getUserId());
    return user.isValidUserSession().then(isValidSession => {
      if (!isValidSession) return false;
      if (isValidSession && instructor.id) return true;
      if (isValidSession && !instructor.id) {
        user.getAttributes().then(data => {
          setInstructor({
            ...instructor,
            id: data["custom:instructor_id"],
            brands: deserializeBrands(data["custom:brand"]),
            adiNumber: data.username,
            firstName: data.given_name,
            lastName: data.family_name,
            status: data["custom:status"] ?? PersonStatus.Active,
          });
        });
        return true;
      }
    });
  };

  const signOut = async () => {
    await user.signOut();
    await client.clearStore();
    setInstructor({
      id: "",
      brands: [Brand.Aads],
      adiNumber: "",
      firstName: "",
      lastName: "",
      mobileNumber: "",
      status: PersonStatus.Active,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        confirmMFA,
        resendMFA,
        resendMFAToResetPassword,
        signUp,
        resetPassword,
        isAuthenticated,
        signOut,
        randomSignIn,
        randomSignInIsAuthenticated,
        randomSignInSignOut,
        instructor,
      }}
      {...props}
    />
  );
};

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
